import { useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BidForm } from '../components/BidForm';
import {
  portalEngagement, portalSubmitBid, portalMessages, portalPostMessage, portalQaList, portalAskQa,
  portalStandards, portalConfirmStandards, portalListFiles, portalUploadFile, portalFileDownloadUrl, portalDeleteFile,
  portalConfirmFinal, portalAcceptance, portalSubmitRemediation, bidTemplateUrl, importBidSheet, fieldErrorsFrom, DELIVERY_STATUS,
  portalCompliance, portalWiList,
  type PortalEngagementDetail, type BidFormPayload, type WorkItem, type EngagementMessage, type PortalQaEntry, type PortalStandards, type PortalAcceptance, type PackageFileMeta, type ApiError,
  type PortalCompliance, COMPLIANCE_NOT_OPEN_REASON,
  type PortalWiRow } from '../lib/api';
import PortalCompliancePanel from '../components/PortalCompliancePanel';
import PortalInstructionsSection from '../components/PortalInstructionsSection';
import { Skeleton } from '../components/ui/Skeleton';
import PortalStandardsWorkflowSection from '../components/PortalStandardsWorkflowSection';
import { ProgressSteps } from '../components/ui/ProgressSteps';
import { toast } from '../components/ui/Toast';
import { fireConfetti } from '../components/ui/confetti';

/** Phase 3 · a labor company's view of one engagement — submit/revise the bid, the two-way message thread,
 *  and shared Q&A. The bid form reuses the same component as the public bid page. */
export default function PortalEngagement() {
  const { eid = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PortalEngagementDetail>({ queryKey: ['portal-engagement', eid], queryFn: () => portalEngagement(eid) });
  const { data: messages } = useQuery<EngagementMessage[]>({ queryKey: ['portal-messages', eid], queryFn: () => portalMessages(eid) });
  const { data: qa } = useQuery<PortalQaEntry[]>({ queryKey: ['portal-qa'], queryFn: portalQaList });
  const { data: standards } = useQuery<PortalStandards>({ queryKey: ['portal-standards', eid], queryFn: () => portalStandards(eid) });
  const { data: acceptance } = useQuery<PortalAcceptance>({ queryKey: ['portal-acceptance', eid], queryFn: () => portalAcceptance(eid) });
  const { data: files } = useQuery<{ requirements: PackageFileMeta[]; mine: PackageFileMeta[] }>({ queryKey: ['portal-files', eid], queryFn: () => portalListFiles(eid) });
  // 履约档案 (Layer 1 step 4A · Eric 9/18). 200 even before 定标 — `open:false` simply hides the tab, so
  // this read can never make the bid page look broken.
  // refetchInterval matches the nav badge (60 s) so the two can never disagree across a Beijing midnight (review 9/18)
  const { data: compliance } = useQuery<PortalCompliance>({ queryKey: ['portal-compliance', eid], queryFn: () => portalCompliance(eid), refetchInterval: 60_000 });
  // 工程指令 (Layer 2 PR9 · Eric 9/18). 200 with an empty list for any engagement this company owns, so
  // like the 履约 read it can never make the bid page look broken. Same 60 s cadence as the nav badge.
  const { data: instructions } = useQuery<{ items: PortalWiRow[] }>({ queryKey: ['portal-work-instructions', eid], queryFn: () => portalWiList(eid), refetchInterval: 60_000 });
  // Tab in the URL (?tab=bid|compliance|instructions), like PackageDetail — so a 待办 deep-link or a
  // refresh reopens the same view. replace:true keeps tab-flipping out of the back button.
  const [searchParams, setSearchParams] = useSearchParams();
  const complianceOpen = compliance?.open === true;
  const rawTab = searchParams.get('tab');
  const wiItems = instructions?.items ?? [];
  // The 工程指令 tab exists once there is an order to show, or once the contract file is open (an order
  // can arrive at any moment after signing, and a tab that appears mid-session is more confusing than a
  // tab that says 「暂无工程指令」).
  const instructionsTabVisible = wiItems.length > 0 || complianceOpen;
  /**
   * DEFAULT TAB (decision, Eric 9/18): 履约 stays the default. 工程指令 wins ONLY when the contractor
   * actually owes something on this engagement — an unread served order, a live quote clock (or one
   * already forfeited, which they must see), or an order waiting on their 认价. Reason: the compliance
   * file is the standing homework and is what they come here for most days; a work instruction is an
   * EVENT, and an event that costs them money if ignored earns the opening screen for as long as it is
   * outstanding. `?tab=…` always wins over both.
   */
  const wiUrgent = wiItems.some((r) => {
    const s = r.instruction.state;
    return (s === 'served' && !r.instruction.read_at)
      || (r.hours_left != null && r.instruction.kind === 'variation')
      || s === 'approved';   // review 9/18 (PR9): a long-dead 失权 order must not pin the landing tab forever
  });
  const tab: 'bid' | 'compliance' | 'instructions' =
    rawTab === 'bid' ? 'bid'
      : rawTab === 'instructions' && instructionsTabVisible ? 'instructions'
        : rawTab === 'compliance' && complianceOpen ? 'compliance'
          : !complianceOpen ? (instructionsTabVisible && wiItems.length > 0 ? 'instructions' : 'bid')
            : wiUrgent ? 'instructions' : 'compliance';
  const setTab = (t: 'bid' | 'compliance' | 'instructions') => setSearchParams((prev) => { prev.set('tab', t); return prev; }, { replace: true });
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [picked, setPicked] = useState<File | null>(null);   // fb_ac916632: pick → confirm, not auto-upload

  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [reply, setReply] = useState('');
  const [question, setQuestion] = useState('');
  const [bidMsg, setBidMsg] = useState<string | null>(null);

  if (isLoading) return <div className="portal"><div className="card"><Skeleton lines={4} /></div></div>;
  if (!data) return <div className="portal"><div className="card">未找到该项目 / engagement not found.</div></div>;
  const { engagement: eng, work_items, last_bid } = data;
  // F2 · round gating (江卫春 6/26): locked after submit, unlocked when staff send the round's feedback.
  const locked = data.can_bid === false;
  const nextRound = data.bid_round ?? ((last_bid?.round_number ?? 0) + 1);
  const prefill = last_bid
    ? { line_items: (last_bid.line_items ?? []).map((l) => ({ work_item_id: l.work_item_id, unit_price_rmb: l.unit_price_rmb, remarks_md: l.remarks_md, item_label: l.item_label, qty: l.qty_quoted })), terms: last_bid.terms }
    : null;

  async function submitBid(payload: BidFormPayload) {
    setBusy(true); setServerError(null); setFieldErrors({}); setBidMsg(null);
    try {
      await portalSubmitBid(eid, payload);
      await qc.invalidateQueries({ queryKey: ['portal-engagement', eid] });
      setBidMsg(`第 ${nextRound} 轮报价已送达，稳了！WTL 审阅后会尽快回复您。 / Round ${nextRound} delivered — WTL will get back to you soon.`);
      toast(`第 ${nextRound} 轮报价已送达 / Round ${nextRound} delivered`, { kind: 'success' });
      void fireConfetti('submit');
    } catch (e) {
      const err = e as ApiError;
      setFieldErrors(fieldErrorsFrom(err));
      setServerError(err?.body?.errors ? null : (err?.message ?? '提交失败 / failed'));
    } finally { setBusy(false); }
  }
  async function postReply() {
    if (!reply.trim()) return;
    await portalPostMessage(eid, reply); setReply('');
    await qc.invalidateQueries({ queryKey: ['portal-messages', eid] });
  }
  async function ask() {
    if (!question.trim()) return;
    await portalAskQa(question); setQuestion('');
    await qc.invalidateQueries({ queryKey: ['portal-qa'] });
  }
  async function confirmStandards() {
    await portalConfirmStandards(eid);
    await qc.invalidateQueries({ queryKey: ['portal-standards', eid] });
  }
  async function submitRemediation(fid: string, notes: string) {
    try {
      await portalSubmitRemediation(eid, fid, notes);
      toast('整改说明已提交，等待复验。 / Remediation submitted — awaiting re-inspection.', { kind: 'success' });
    } catch (e) {
      toast((e as ApiError)?.body?.error ?? '提交失败 / failed', { kind: 'error' });
    }
    await qc.invalidateQueries({ queryKey: ['portal-acceptance', eid] });
  }
  async function confirmFinal() {
    try {
      // bind consent to the total rendered on THIS page — a stale view 409s instead of locking silently
      await portalConfirmFinal(eid, eng.final_total_rmb ?? undefined);
    } catch (e) {
      toast((e as ApiError)?.body?.error ?? '确认失败，请刷新后重试。 / Confirm failed — refresh and retry.', { kind: 'error' });
    }
    await qc.invalidateQueries({ queryKey: ['portal-engagement', eid] });
    await qc.invalidateQueries({ queryKey: ['portal-messages', eid] });
  }
  // fb_ac916632 (江卫春): pick a file, then explicitly confirm — so an upload is a deliberate "this is the
  // updated file", not a silent on-change. A wrongly-picked file can be cleared before confirming.
  function onPick(e: React.ChangeEvent<HTMLInputElement>) { setPicked(e.target.files?.[0] ?? null); }
  async function confirmUpload() {
    if (!picked) return;
    setUploading(true);
    try { await portalUploadFile(eid, picked, 'quote'); await qc.invalidateQueries({ queryKey: ['portal-files', eid] }); setPicked(null); if (fileRef.current) fileRef.current.value = ''; }
    finally { setUploading(false); }
  }
  async function removeFile(fileId: string) {
    if (!window.confirm('删除该附件？/ Remove this file?')) return;
    await portalDeleteFile(eid, fileId);
    await qc.invalidateQueries({ queryKey: ['portal-files', eid] });
  }

  // fb_07c3fe4a · 投标截止时间: past due, the server refuses online bids AND file uploads — mirror that here.
  const dueAt: string | null = (eng as any).tender_due_at ?? null;
  const pastDue = !!dueAt && new Date(dueAt).getTime() < Date.now();

  // display-only progress derived from existing fields — no new data flow
  const hasBid = !!last_bid || locked;
  const finalRequested = !!eng.final_confirm_requested_at;
  const finalLocked = !!eng.final_confirmed_at;
  const awarded = eng.award_status === 'awarded';
  return (
    <div className="portal">
      <div className="row" style={{ marginBottom: 8 }}><Link to="/portal">← 返回 / Back</Link></div>
      <h2>{eng.package_name}</h2>
      {eng.scope_summary_md && <p className="muted small">{eng.scope_summary_md}</p>}
      <ProgressSteps steps={[
        { label: '报价 / Bid', state: hasBid ? 'done' : 'active' },
        { label: '议价 / Negotiate', state: finalRequested ? 'done' : hasBid ? 'active' : 'todo' },
        { label: '锁定 / Lock', state: finalLocked ? 'done' : finalRequested ? 'active' : 'todo' },
        { label: '中标 / Award', state: awarded ? 'done' : finalLocked ? 'active' : 'todo' },
      ]} />

      {/* 履约档案 tab row — only after 定标 + 合同存在 (Layer 1 step 4A). Before that the page is exactly
          what it always was: one bid view, no tabs, no new chrome. */}
      {/* review 9/18: a contractor who KNOWS he signed must not see 待办 N in the header and no tab — say why */}
      {compliance && !compliance.open && compliance.reason && compliance.reason !== 'not_awarded' && (
        <div className="muted small" style={{ margin: '8px 0 4px' }}>📁 履约 / Compliance：{COMPLIANCE_NOT_OPEN_REASON[compliance.reason]}</div>
      )}
      {(complianceOpen || instructionsTabVisible) && (
        <div className="row tab-row" style={{ gap: 6, margin: '10px 0 4px', flexWrap: 'wrap' }}>
          {complianceOpen && (
            <button className={`tab${tab === 'compliance' ? ' active' : ''}`} onClick={() => setTab('compliance')}>
              📁 履约 / Compliance
              {(compliance?.todo.total ?? 0) > 0 && <span className="pill pill-bad small" style={{ marginLeft: 6 }}>待办 {compliance!.todo.total}</span>}
            </button>
          )}
          {instructionsTabVisible && (
            <button className={`tab${tab === 'instructions' ? ' active' : ''}`} onClick={() => setTab('instructions')}>
              📝 工程指令 / Instructions
              {wiItems.length > 0 && <span className={`pill ${wiUrgent ? 'pill-bad' : 'pill-muted'} small`} style={{ marginLeft: 6 }}>{wiItems.length}</span>}
            </button>
          )}
          <button className={`tab${tab === 'bid' ? ' active' : ''}`} onClick={() => setTab('bid')}>📊 报价与沟通 / Bid &amp; messages</button>
        </div>
      )}

      {tab === 'compliance' && compliance?.open && <PortalCompliancePanel eid={eid} data={compliance} />}
      {tab === 'instructions' && <PortalInstructionsSection eid={eid} />}

      {tab === 'bid' && <>
      {/* fb_07c3fe4a · 投标截止时间 — labor sees the deadline up front; past due the channel is closed. */}
      {dueAt && (
        <div className="card small" style={{ margin: '8px 0', borderLeft: `3px solid ${pastDue ? 'var(--bad)' : 'var(--accent)'}`, color: pastDue ? 'var(--bad)' : undefined }}>
          ⏰ 投标截止 / Bid deadline：{new Date(dueAt).toLocaleString('zh-CN')}
          {pastDue && <strong>　· 已截止 — 在线报价与文件上传已关闭 / closed: bidding & uploads locked</strong>}
        </div>
      )}

      {/* 议价最终价确认 (改改 7/1 · Phase B) — confirm the staff-authored final offer; full line prices shown. */}
      {eng.final_confirm_requested_at && (
        <div className="card" style={{ margin: '10px 0', borderLeft: '3px solid var(--good)' }}>
          {eng.final_confirmed_at ? (
            <div className="small" style={{ color: 'var(--good)' }}>
              🔒 您已确认最终总价 <span className="money-lg" style={{ fontSize: 16 }}>¥{Math.round(eng.final_total_rmb ?? 0).toLocaleString('zh-CN')}</span>
              {eng.final_tax_pct != null && <>（不含税 · 税率 {eng.final_tax_pct}% · 含税 ¥{Math.round((eng.final_total_rmb ?? 0) * (100 + eng.final_tax_pct) / 100).toLocaleString('zh-CN')}）</>}
              ，价格已锁定 / Final total confirmed & locked（{String(eng.final_confirmed_at).slice(0, 16).replace('T', ' ')}）
              <div className="muted" style={{ marginTop: 4 }}>感谢确认，签约事宜 WTL 会尽快与您联系。 / Thank you — WTL will follow up on contracting.</div>
            </div>
          ) : (
            <>
              <div className="small"><strong>请确认最终报价 / Confirm the final offer</strong></div>
              <div className="muted small" style={{ margin: '4px 0 8px' }}>
                招标方已按议价结果拟定最终报价，确认后价格即锁定。/ WTL has drafted the negotiated final offer — confirming locks the price.
              </div>
              {/* the final-offer round's full line prices (what you're agreeing to) */}
              {eng.final_offer_bid_id && last_bid && last_bid.id === eng.final_offer_bid_id && (
                <table className="cmp" style={{ fontSize: 12, marginBottom: 8 }}>
                  <thead><tr><th>项目 / Item</th><th>工程量</th><th>最终单价 / Final</th><th>小计 / Amount</th></tr></thead>
                  <tbody>
                    {(last_bid.line_items ?? []).map((l) => {
                      const w = work_items.find((x) => x.id === l.work_item_id);
                      return (
                        <tr key={l.id}>
                          <td>{w?.item_label ?? l.item_label ?? '—'}</td>
                          <td className="num">{l.work_item_id ? (w?.qty ?? '—') : (l.qty_quoted ?? '—')}{w?.unit ?? ''}</td>
                          <td className="num">{l.unit_price_rmb != null ? `¥${l.unit_price_rmb.toLocaleString('zh-CN')}` : '—'}</td>
                          <td className="num">{l.amount_rmb != null ? `¥${Math.round(l.amount_rmb).toLocaleString('zh-CN')}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr><td colSpan={3} style={{ textAlign: 'right' }}><strong>最终合计（不含税）/ Final total (ex-tax)</strong></td><td className="num money-lg" style={{ fontSize: 15 }}><strong>¥{Math.round(eng.final_total_rmb ?? 0).toLocaleString('zh-CN')}</strong></td></tr></tfoot>
                </table>
              )}
              {/* fb_e5f31dc1 · 列明三项：不含税金额 · 税金点 · 含税金额 */}
              {eng.final_tax_pct != null && eng.final_total_rmb != null && (
                <div className="small" style={{ marginBottom: 8 }}>
                  不含税 <strong>¥{Math.round(eng.final_total_rmb).toLocaleString('zh-CN')}</strong>
                  　· 税率 <strong>{eng.final_tax_pct}%</strong>
                  　· 含税 <strong>¥{Math.round(eng.final_total_rmb * (100 + eng.final_tax_pct) / 100).toLocaleString('zh-CN')}</strong>
                </div>
              )}
              <button className="btn-good" onClick={confirmFinal}>✅ 确认最终报价 / Confirm final offer</button>
              <div className="muted small" style={{ marginTop: 6 }}>如有异议请在下方「沟通」留言，招标方可撤回重拟。/ Disagree? Reply below — WTL can withdraw and redraft.</div>
            </>
          )}
        </div>
      )}

      <h3 style={{ marginTop: 14 }}>招标文件 / Tender documents</h3>
      <div className="card" style={{ marginBottom: 8 }}>
        {(files?.requirements ?? []).length === 0 && <div className="muted small">暂无招标文件 — WTL 发布后这里会出现。 / No documents yet — they'll appear here once WTL posts them.</div>}
        {(files?.requirements ?? []).map((f) => (
          <div key={f.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
            {f.category && <span className="pill small pill-muted">{f.category}</span>}
            <a href={portalFileDownloadUrl(eid, f.id)} style={{ flex: 1 }}>{f.file_name}</a>
          </div>
        ))}
        <div style={{ marginTop: 8, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>上传我的报价 / 文件 / Upload my quote &amp; documents</div>
          {pastDue ? (
            <div className="small" style={{ color: 'var(--bad)' }}>⏰ 投标已截止，无法再上传文件。/ Tender closed — uploads are locked.</div>
          ) : (
            <div className="row-wrap" style={{ gap: 8, alignItems: 'center' }}>
              <input ref={fileRef} type="file" onChange={onPick} disabled={uploading} />
              {picked && <button onClick={confirmUpload} disabled={uploading}>{uploading ? '上传中… / uploading…' : '✓ 确认上传 / Confirm upload'}</button>}
              {picked && !uploading && <button className="small" onClick={() => { setPicked(null); if (fileRef.current) fileRef.current.value = ''; }}>取消 / Cancel</button>}
            </div>
          )}
          {picked && <div className="small muted" style={{ marginTop: 2 }}>待上传 / to upload：{picked.name}</div>}
          {(files?.mine ?? []).map((f) => (
            <div key={f.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
              <span className="pill small pill-good">✓ 已送达 / delivered</span>
              <a href={portalFileDownloadUrl(eid, f.id)} style={{ flex: 1 }}>{f.file_name}</a>
              <button className="small" title="删除 / remove" onClick={() => removeFile(f.id)}>✕ 删除 / Remove</button>
            </div>
          ))}
        </div>
      </div>

      {/* Phase B · while a final offer is pending/locked the labor party confirms — no re-quoting. */}
      {!eng.final_offer_bid_id && <>
      <h3>报价 / Bid（第 {nextRound} 轮 / round {nextRound}）</h3>
      {(() => {
        const rev = (eng as any).boq_revision ?? 0;
        const changed = rev > 0 ? work_items.filter((w) => (w.changed_in_revision ?? 0) === rev) : [];
        if (!changed.length) return null;
        return (
          <div className="card" style={{ marginBottom: 8, borderLeft: '3px solid var(--warn)' }}>
            <div className="small"><strong>清单已更新至第 {rev} 版</strong> — 请重点针对以下调整项重新报价，其余沿用上一轮。 / List revised — re-quote only the changed items below; the rest carry forward.</div>
            <ul className="small" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {changed.map((w) => <li key={w.id}>{w.item_label}{w.revision_note ? <span className="muted">（{w.revision_note}）</span> : null}</li>)}
            </ul>
          </div>
        );
      })()}
      {bidMsg && <div className="small" style={{ color: 'var(--good)', marginBottom: 6 }}>{bidMsg}</div>}
      <BidForm
        workItems={work_items as unknown as WorkItem[]}
        busy={busy}
        submitLabel={`提交第 ${nextRound} 轮 / Submit round ${nextRound}`}
        onSubmit={submitBid}
        serverError={serverError}
        fieldErrors={fieldErrors}
        prefill={prefill}
        roundNumber={nextRound}
        exportUrl={bidTemplateUrl(eid)}
        onImportSheet={(file) => importBidSheet(eid, file)}
        readOnly={locked || pastDue}   /* review find: the deadline banner said bidding closed but the form stayed editable — submit would only 409 after filling everything in */
        lockedRound={last_bid?.round_number}
      />
      </>}

      {/* 验收标准确认书 pre-contract workflow (PM-drafted standards → Q&A → dual-confirmed annexure).
          When a workflow exists it supersedes the legacy subcontract checkbox below. */}
      <PortalStandardsWorkflowSection eid={eid} />

      {standards?.subcontract_id && standards.criteria.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3>验收标准确认 / Confirm acceptance standards</h3>
          <div className="card" style={{ marginBottom: 8 }}>
            <p className="muted small">签约前请确认您已了解以下分工种验收标准。/ Confirm you understand each trade's acceptance standard before the contract can be signed.</p>
            {standards.criteria.map((c) => (
              <div key={c.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--rule)' }}>
                <div className="small"><strong>{c.label_zh || c.trade}</strong>{c.is_critical && <span className="pill small pill-bad" style={{ marginLeft: 6 }}>关键 / critical</span>}</div>
                {c.pass_rule_md && <div className="muted small" style={{ marginTop: 2 }}>{c.pass_rule_md}</div>}
              </div>
            ))}
            {standards.confirmed_at
              ? <div className="small" style={{ color: 'var(--good)', marginTop: 8 }}>✓ 已确认 / confirmed（{standards.confirmed_at.slice(0, 16).replace('T', ' ')}）</div>
              : <button style={{ marginTop: 8 }} onClick={confirmStandards}>我已了解并确认 / I understand and confirm</button>}
          </div>
        </div>
      )}

      {acceptance?.subcontract_id && acceptance.findings.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3>验收整改 / Acceptance remediation</h3>
          <div className="card" style={{ marginBottom: 8 }}>
            <p className="muted small">验收中提出的问题项。整改完成后请填写说明提交，项目方会安排复验。/ Findings raised at acceptance — describe what you fixed and submit for re-inspection.</p>
            {acceptance.findings.map((f) => {
              const crit = acceptance.criteria.find((c) => c.id === f.criterion_id);
              return (
                <div key={f.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--rule)' }}>
                  <div className="small">
                    <strong>{crit?.label_zh || crit?.trade || '问题项'}</strong>
                    <span className="muted"> · 第 {f.iteration} 次</span>
                    {f.resolution_at
                      ? <span className="pill small pill-good" style={{ marginLeft: 6 }}>已提交整改 ✓</span>
                      : <span className="pill small pill-warn" style={{ marginLeft: 6 }}>待整改</span>}
                  </div>
                  {f.notes_md && <div className="muted small" style={{ marginTop: 2 }}>问题 / issue：{f.notes_md}</div>}
                  {f.resolution_at
                    ? (f.resolution_notes_md && <div className="muted small" style={{ marginTop: 2 }}>整改说明 / fix：{f.resolution_notes_md}</div>)
                    : <RemediationForm onSubmit={(notes) => void submitRemediation(f.id, notes)} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h3 style={{ marginTop: 18 }}>沟通 / Messages</h3>
      <div className="card" style={{ marginBottom: 8 }}>
        {(messages ?? []).length === 0 && <div className="muted small">还没有消息。有事随时留言，WTL 都看得到。 / No messages yet — write anytime, WTL reads everything.</div>}
        {(messages ?? []).map((m) => (
          <div key={m.id} className={`bubble ${m.direction === 'inbound' ? 'me' : 'them'}`}>
            <div className="meta">
              {m.direction === 'inbound' ? '我方 / us' : 'WTL'} · {m.created_at.slice(5, 16)}
              {m.direction === 'outbound' && DELIVERY_STATUS[m.delivery_status] && <span className={`pill small ${DELIVERY_STATUS[m.delivery_status].pill}`} style={{ marginLeft: 6 }}>{DELIVERY_STATUS[m.delivery_status].label}</span>}
            </div>
            {m.body_md}
          </div>
        ))}
      </div>
      <div className="row-wrap" style={{ gap: 6 }}>
        <input className="grow" style={{ minWidth: 160 }} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="回复 WTL / reply" />
        <button disabled={!reply.trim()} onClick={postReply}>发送 / Send</button>
      </div>

      <h3 style={{ marginTop: 18 }}>答疑 / Q&amp;A</h3>
      <div className="row-wrap" style={{ gap: 6, marginBottom: 8 }}>
        <input className="grow" style={{ minWidth: 160 }} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="有疑问尽管问，WTL 审核后统一答复 / ask away — answers are shared with all bidders" />
        <button disabled={!question.trim()} onClick={ask}>提交 / Ask</button>
      </div>
      {(qa ?? []).map((q) => (
        <div key={q.id} className="card" style={{ marginBottom: 6 }}>
          <div className="small"><strong>Q:</strong> {q.q_md}</div>
          {q.a_md && <div className="small" style={{ marginTop: 2 }}><strong>A:</strong> {q.a_md}</div>}
        </div>
      ))}
      </>}
    </div>
  );
}

/** One-line remediation composer (st.15) — notes are required; the server 400s an empty claim. */
function RemediationForm({ onSubmit }: { onSubmit: (notes: string) => void }) {
  const [notes, setNotes] = useState('');
  return (
    <div className="row" style={{ gap: 6, marginTop: 4 }}>
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="整改说明（必填）/ what was fixed"
        style={{ flex: 1 }}
      />
      <button disabled={!notes.trim()} onClick={() => { onSubmit(notes.trim()); setNotes(''); }}>提交整改 / Submit</button>
    </div>
  );
}
